import React, { FC, useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import styles from "../Admin/Admin.module.scss";
import "../old/Payment/Payment.css";
import apiInstance from "../services/httpService";

// ── Constants ────────────────────────────────────────────────────────────────

const MONTHS: Record<string, string> = {
  "01": "JAN", "02": "FEB", "03": "MAR", "04": "APR", "05": "MAY", "06": "JUN",
  "07": "JUL", "08": "AUG", "09": "SEP", "10": "OCT", "11": "NOV", "12": "DEC",
};

const VOCAL_PARTS = ["soprano", "alto", "tenor", "bass"] as const;
type VocalPart = typeof VOCAL_PARTS[number];

// ── Types ────────────────────────────────────────────────────────────────────

interface EventItem {
  event_id: number;
  name: string;
  display_name: string;
  event_thumbnail: string | null;
  date_details: { date: string; dummy_date: boolean };
}

interface AttendanceRecord {
  user_id:         number;
  name:            string;
  vocal_part:      string | null;
  present:         number;
  od:              number;
  absent:          number;
  total_practices: number;
  daily:           Record<string, "present" | "absent" | "od" | "unmarked">;
}

interface ReportData {
  event_id:        number;
  season_name:     string;
  total_practices: number;
  practice_dates:  string[];   // ISO YYYY-MM-DD, sorted asc
  attendance:      AttendanceRecord[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const pctColour = (pct: number) => pct >= 80 ? "#2e7d32" : "#c62828";

// Format ISO date "YYYY-MM-DD" → "10 Jan"
const fmtDateHeader = (iso: string) => {
  const [, m, d] = iso.split("-").map(Number);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d} ${months[m - 1]}`;
};

// Coloured tick / cross / dash for a daily status cell
const StatusCell = ({ status }: { status?: string }) => {
  if (status === "present" || status === "od")
    return <span style={{ color: "#2e7d32", fontWeight: "bold", fontSize: "1rem" }}>✓</span>;
  if (status === "absent")
    return <span style={{ color: "#c62828", fontWeight: "bold", fontSize: "1rem" }}>✗</span>;
  return <span style={{ color: "#bbb" }}>—</span>;  // unmarked / unknown
};

// ── Component ────────────────────────────────────────────────────────────────

const AttendanceSummary: FC = () => {

  // Events list
  const [events,  setEvents]  = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // Selected event detail
  const [selectedEvent,  setSelectedEvent]  = useState<EventItem | null>(null);
  const [reportData,     setReportData]     = useState<ReportData | null>(null);
  const [reportLoading,  setReportLoading]  = useState(false);
  const [reportError,    setReportError]    = useState<string | null>(null);
  const [activeTab,      setActiveTab]      = useState<VocalPart>("soprano");
  // { col, dir } — null means default (name A→Z)
  const [sortState, setSortState] = useState<{ col: "name" | "pct"; dir: "asc" | "desc" } | null>(null);

  // ── Shared: open an event's detail view + fetch its report ─────────────────
  const openEventDetail = (event: EventItem) => {
    setSelectedEvent(event);
    setReportData(null);
    setReportError(null);
    setReportLoading(true);
    setActiveTab("soprano");
    setSortState(null);

    const token = localStorage.getItem("token");
    apiInstance
      .axiosCall(
        "api/ccc/practice/attendance/report",
        { token, event_id: event.event_id },
        "POST"
      )
      .then((res: any) => {
        if (res?.data?.status === "success") {
          setReportData(res.data.data);
        } else {
          setReportError(res?.data?.message || "Could not load attendance report.");
        }
        setReportLoading(false);
      })
      .catch(() => {
        setReportError("Could not load attendance report. Please try again.");
        setReportLoading(false);
      });
  };

  // ── Fetch event list — auto-open if exactly one event ─────────────────────
  useEffect(() => {
    apiInstance
      .axiosCall("api/ccc/events/non-subscription", {}, "GET")
      .then((res: any) => {
        if (res?.data?.status === "success") {
          const data: EventItem[] = res.data.data || [];
          setEvents(data);
          if (data.length === 1) {
            // Only one active event — skip the card grid and open it directly.
            openEventDetail(data[0]);
          }
        } else {
          setError(res?.data?.message || "Could not load events.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load events. Please try again.");
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selectedEvent) {
    // Filter members for the active tab, then sort
    const pct = (r: AttendanceRecord) =>
      r.total_practices > 0 ? (r.present + r.od) / r.total_practices : 0;

    const tabMembers: AttendanceRecord[] = reportData
      ? [...reportData.attendance]
          .filter((r) => (r.vocal_part ?? "").toLowerCase() === activeTab)
          .sort((a, b) => {
            const col = sortState?.col ?? "name";
            const dir = sortState?.dir ?? "asc";
            if (col === "name") {
              const cmp = a.name.localeCompare(b.name);
              return dir === "asc" ? cmp : -cmp;
            }
            // col === "pct"
            const cmp = pct(a) - pct(b);
            return dir === "asc"
              ? cmp || a.name.localeCompare(b.name)
              : -cmp || a.name.localeCompare(b.name);
          })
      : [];

    // Cycle: null → asc → desc → null; switching column resets to asc.
    const cycleSort = (col: "name" | "pct") =>
      setSortState((prev) => {
        if (prev?.col !== col) return { col, dir: "asc" };
        if (prev.dir === "asc") return { col, dir: "desc" };
        return null;
      });

    const sortIcon = (col: "name" | "pct") => {
      if (sortState?.col !== col) return "⇅";
      return sortState.dir === "asc" ? "▲" : "▼";
    };

    // Which tabs have at least one member
    const populatedTabs = VOCAL_PARTS.filter((vp) =>
      reportData?.attendance.some(
        (r) => (r.vocal_part ?? "").toLowerCase() === vp
      )
    );

    return (
      <div style={{ padding: "0 1.5rem 2rem" }}>

        {/* Back link */}
        <button
          type="button"
          onClick={() => setSelectedEvent(null)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "Montserrat-Regular", fontSize: "0.85rem",
            color: "#3a8bac", padding: "0", marginBottom: "1rem",
            display: "inline-flex", alignItems: "center", gap: "4px",
          }}
        >
          ← Back to events
        </button>

        {/* Event heading */}
        <div className={styles.Admin__form} style={{ marginBottom: "1rem" }}>
          <label className={styles.Admin__form_label}>
            {selectedEvent.display_name}
          </label>

          {reportData && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "0.5rem" }}>
              <span style={{
                fontFamily: "Montserrat-Regular", fontSize: "0.85rem", color: "#555",
              }}>
                Practice sessions
              </span>
              <div style={{
                display: "inline-flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #3a8bac 0%, #195d7a 100%)",
                color: "#fff",
                borderRadius: "10px",
                padding: "6px 18px",
                minWidth: "56px",
                boxShadow: "0 2px 8px rgba(58,139,172,0.35)",
              }}>
                <span style={{
                  fontFamily: "Montserrat-SemiBold", fontSize: "1.5rem",
                  lineHeight: 1,
                }}>
                  {reportData.total_practices}
                </span>
                <span style={{
                  fontFamily: "Montserrat-Regular", fontSize: "0.65rem",
                  opacity: 0.85, letterSpacing: "0.04em", marginTop: "2px",
                }}>
                  TOTAL
                </span>
              </div>
            </div>
          )}
        </div>

        {reportLoading ? (
          <div className={styles.Admin__loading}><CircularProgress /></div>
        ) : reportError ? (
          <div className={styles.Admin__empty}>{reportError}</div>
        ) : reportData ? (
          <>
            {/* Vocal-part tabs — only show tabs that have members */}
            <div className={styles.Admin__users_tabs}>
              {(populatedTabs.length > 0 ? populatedTabs : VOCAL_PARTS).map((vp) => (
                <button
                  key={vp}
                  type="button"
                  className={`${styles.Admin__users_tab} ${
                    activeTab === vp ? styles.Admin__users_tab_active : ""
                  }`}
                  onClick={() => { setActiveTab(vp); setSortState(null); }}
                >
                  {vp.charAt(0).toUpperCase() + vp.slice(1)}
                </button>
              ))}
            </div>

            {/* Attendance table */}
            {tabMembers.length === 0 ? (
              <div className={styles.Admin__empty}>
                No {activeTab} members enrolled for this event.
              </div>
            ) : (
              <div style={{ overflowX: "auto", marginTop: "0.75rem" }}>
                <table className={styles.Admin__attendance_table} style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th
                        onClick={() => cycleSort("name")}
                        style={{ textAlign: "left", cursor: "pointer", userSelect: "none",
                          whiteSpace: "nowrap", minWidth: "140px" }}
                        title="Sort by name"
                      >
                        Name {sortIcon("name")}
                      </th>

                      {/* One column per practice date */}
                      {(reportData?.practice_dates ?? []).map((d) => (
                        <th key={d} style={{ whiteSpace: "nowrap", minWidth: "60px" }}>
                          {fmtDateHeader(d)}
                        </th>
                      ))}

                      <th
                        onClick={() => cycleSort("pct")}
                        style={{ cursor: "pointer", userSelect: "none",
                          whiteSpace: "nowrap", minWidth: "90px" }}
                        title="Sort by % attended"
                      >
                        % {sortIcon("pct")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabMembers.map((rec) => {
                      const attended = rec.present + rec.od;
                      const pct      = rec.total_practices > 0
                        ? Math.round((attended / rec.total_practices) * 100)
                        : 0;
                      return (
                        <tr key={rec.user_id}>
                          <td style={{ textAlign: "left", fontFamily: "Montserrat-Regular",
                            fontSize: "0.85rem" }}>
                            {rec.name}
                          </td>

                          {/* Status cell per practice date */}
                          {(reportData?.practice_dates ?? []).map((d) => (
                            <td key={d} style={{ textAlign: "center" }}>
                              <StatusCell status={rec.daily?.[d]} />
                            </td>
                          ))}

                          <td style={{ color: pctColour(pct),
                            fontFamily: "Montserrat-SemiBold", whiteSpace: "nowrap" }}>
                            {pct}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    );
  }

  // ── Events list ───────────────────────────────────────────────────────────
  if (loading) {
    return <div className={styles.Admin__loading}><CircularProgress /></div>;
  }

  if (error) {
    return <div className={styles.Admin__empty}>{error}</div>;
  }

  if (events.length === 0) {
    return <div className={styles.Admin__empty}>No active events.</div>;
  }

  return (
    <div style={{ padding: "0 1.5rem 2rem" }}>
      <div className="row event-item-row">
        {events.map((event) => {
          const parts = (event.date_details?.date || "//").split("/");
          return (
            <div
              className="col-lg-4 my-3 event-item-wrapper"
              key={"summary-" + event.event_id}
              onClick={() => openEventDetail(event)}
              style={{ cursor: "pointer" }}
            >
              <div className="event-item">
                <img
                  className="event-img"
                  alt=""
                  src={
                    event.event_thumbnail
                      ? event.event_thumbnail
                      : require("../../assets/Images/Ladies.jpg")
                  }
                />
                <div className="row event-info p-3">
                  <div className="col-lg-3 col-3 text-center mb-3">
                    <h3 className="m-0 mt-1 event-month">{MONTHS[parts[1]]}</h3>
                    {event.date_details?.dummy_date === false && (
                      <h3 className="m-0 event-date">{parts[0]}</h3>
                    )}
                    <h3 className="m-0 mt-1 event-year">{parts[2]}</h3>
                  </div>
                  <div className="col-lg-9 col-9">
                    <h5 className="event-title" title={event.name}>
                      {event.display_name}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AttendanceSummary;
